import { Injectable } from "@angular/core";
import { DeviceService } from "@mydocma/core/src/lib/device";
import { findTextEntryTarget, ListenerGroup } from "../shared/keyboard-viewport.util";

const TAP_SLOP_PX = 12;

// On focus WebKit pans the document to center the field, scrolling past the document end — a
// white strip above the keyboard that grows with every field hop. Prevented by suppressing the
// native tap focus and refocusing with preventScroll:true (iOS honors the flag).
@Injectable({ providedIn: 'root' })
export class RpKeyboardFocusGuardService {

    private listeners = new ListenerGroup();
    private tapStart: { x: number; y: number } | null = null;

    constructor(private readonly deviceService: DeviceService) {
    }

    start(): void {
        if (this.listeners.active || typeof document === 'undefined') {
            return;
        }
        this.listeners.add(document, 'touchstart', event => this.onTouchStart(event as TouchEvent), { capture: true, passive: true });
        this.listeners.add(document, 'touchend', event => this.onTouchEnd(event as TouchEvent), { capture: true, passive: false });
    }

    private onTouchStart(event: TouchEvent): void {
        this.tapStart = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }

    private onTouchEnd(event: TouchEvent): void {
        const start = this.tapStart;
        this.tapStart = null;
        if (!start) {
            return;
        }
        const touch = event.changedTouches[0];
        if (!touch || Math.abs(touch.clientX - start.x) > TAP_SLOP_PX || Math.abs(touch.clientY - start.y) > TAP_SLOP_PX) {
            return;
        }
        const select = event.target instanceof Element ? event.target.closest('ng-select') : null;
        if (select) {
            // dropdown inputs open their panel (tablet) or fullscreen popup (phone) on CLICK, which
            // preventDefault on the touchend would swallow — so they are left to their own handlers.
            // EXCEPT the phone popup flow: there the native tap first focuses the search input, the
            // keyboard reveal moves the field inside iOS's touchend→click window, and the synthesized
            // click retargets to body — the wrapper's (click) never fires and the popup intermittently
            // fails to open. Suppress the native flow entirely (no focus, no keyboard) and deliver the
            // click synchronously to the still-unmoved ng-select instead.
            // the clear cross acts on mousedown, which the preventDefault below would kill
            if (this.deviceService.isMobil() && !(event.target as Element).closest('.ng-clear-wrapper')) {
                event.preventDefault();
                (event.target as Element).dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
            }
            return;
        }
        const editable = findTextEntryTarget(event.target);
        if (!editable || editable === document.activeElement) {
            return;
        }
        event.preventDefault();
        editable.focus({ preventScroll: true });
    }
}
